import type { LucideIcon } from "lucide-react";
import {
  HeartPulse,
  Scale,
  Sparkles,
  SunMedium,
  Wrench,
  Shield,
  ShieldAlert,
  Users,
  UserRound,
  MessagesSquare,
  Landmark,
  History,
  LineChart,
  BookOpen,
  UsersRound,
  UserPlus,
  MessageCirclePlus,
  Flame,
  GraduationCap,
  Layers,
} from "lucide-react";
import type { ChatEntry } from "@/types/chat";

export type SidebarNavLeafItem =
  | {
      id: string;
      kind: "chat";
      label: string;
      entry: ChatEntry;
      icon: LucideIcon;
    }
  | {
      id: string;
      kind: "link";
      label: string;
      to: string;
      icon: LucideIcon;
      /** When true, `location.pathname.startsWith(to)` counts as active (e.g. `/resources/...`). */
      matchPrefix?: boolean;
    };

export type SidebarNavGroupItem = {
  id: string;
  kind: "group";
  label: string;
  icon: LucideIcon;
  defaultExpanded?: boolean;
  children: SidebarNavLeafItem[];
};

export type SidebarNavItem = SidebarNavLeafItem | SidebarNavGroupItem;

export type SidebarNavSection = {
  id: string;
  label: string;
  /** When false, section starts collapsed on first visit. Defaults to true. */
  defaultExpanded?: boolean;
  items: SidebarNavItem[];
};

export const SIDEBAR_NAV_SECTIONS: SidebarNavSection[] = [
  {
    id: "supports",
    label: "Supports",
    defaultExpanded: false,
    items: [
      { id: "get-coaching", kind: "chat", label: "My Coach", entry: "get_coaching", icon: HeartPulse },
      {
        id: "wellness-supports",
        kind: "group",
        label: "Wellness Supports",
        icon: Sparkles,
        defaultExpanded: false,
        children: [
          { id: "reset-nervous", kind: "chat", label: "Reset my Nervous System", entry: "reset_my_nervous_system", icon: Sparkles },
          { id: "reclaim-balance", kind: "chat", label: "Reclaim my Balance", entry: "reclaim_my_balance", icon: Scale },
          { id: "expand-impact", kind: "chat", label: "Expand my Impact", entry: "expand_my_impact", icon: SunMedium },
          { id: "renew-spark", kind: "chat", label: "Renew my Spark", entry: "renew_my_spark", icon: Flame },
        ],
      },
    ],
  },
  {
    id: "tools",
    label: "Tools",
    defaultExpanded: true,
    items: [
      {
        id: "teacher-scenarios",
        kind: "link",
        label: "Scenario Practice",
        to: "/tools/teacher-scenarios",
        icon: GraduationCap,
        matchPrefix: true,
      },
      {
        id: "think-decision",
        kind: "link",
        label: "Strategic Decision Partner",
        to: "/tools/think-through-decision",
        icon: Wrench,
      },
      {
        id: "behavior-incident",
        kind: "link",
        label: "Behavior Incident Toolkit",
        to: "/restoration-room",
        icon: ShieldAlert,
      },
    ],
  },
  {
    id: "conversation-lab",
    label: "Conversation Lab",
    defaultExpanded: false,
    items: [
      {
        id: "practice-conversations",
        kind: "group",
        label: "Practice Conversations",
        icon: Layers,
        defaultExpanded: false,
        children: [
          { id: "lab-students", kind: "chat", label: "Connecting with Students", entry: "connect_with_students", icon: Users },
          { id: "lab-parents", kind: "chat", label: "Parents", entry: "strengthen_my_voice", icon: UserRound },
          { id: "lab-colleagues", kind: "chat", label: "Colleagues", entry: "lead_with_clarity", icon: MessagesSquare },
          { id: "lab-board", kind: "chat", label: "Effective Board Communication", entry: "navigate_board_relationships", icon: Landmark },
        ],
      },
    ],
  },
  {
    id: "growth",
    label: "Growth",
    defaultExpanded: true,
    items: [
      { id: "history", kind: "link", label: "History", to: "/history", icon: History },
      {
        id: "insights",
        kind: "link",
        label: "Insights",
        to: "/growth/insights",
        icon: LineChart,
      },
      {
        id: "resources",
        kind: "link",
        label: "Resources",
        to: "/resources",
        icon: BookOpen,
        matchPrefix: true,
      },
    ],
  },
  {
    id: "admin",
    label: "Admin",
    defaultExpanded: true,
    items: [
      { id: "my-teams", kind: "link", label: "My Teams", to: "/admin/my-teams", icon: UsersRound },
      { id: "invite", kind: "link", label: "Invite Teammates", to: "/admin/invite-teammates", icon: UserPlus },
    ],
  },
];

export const SIDEBAR_NEW_CHAT = {
  label: "New Chat",
  icon: MessageCirclePlus,
  entry: "ai_mentor" as ChatEntry,
};

/** Shown only to platform admins (`profiles.is_super_admin`). */
export const SYSTEM_ADMIN_NAV_ITEM: SidebarNavLeafItem = {
  id: "system-admin",
  kind: "link",
  label: "Platform admin",
  to: "/admin/system",
  icon: Shield,
};

/** Flatten nested nav items to leaf items (for access matrix, etc.). */
export function flattenSidebarNavItems(items: SidebarNavItem[]): SidebarNavLeafItem[] {
  const leaves: SidebarNavLeafItem[] = [];
  for (const item of items) {
    if (item.kind === "group") {
      leaves.push(...item.children);
    } else {
      leaves.push(item);
    }
  }
  return leaves;
}
